import {ALPINE_REGION,routeCenter,routePolyline,routeProgress} from './alpine-region.js';

const lines={valley:routePolyline('valley'),pines:routePolyline('pines')};
const all=Object.values(lines).flat(),minX=Math.min(...all.map(p=>p.x))-12,maxX=Math.max(...all.map(p=>p.x))+12;
const startZ=ALPINE_REGION.start.z,finishZ=ALPINE_REGION.finish.z;
// A deliberately schematic lateral scale keeps the two branches legible.
// Positions and progress still come from the same authored route as gameplay.
const sx=112/(maxX-minX),sz=170/(finishZ-startZ);
export function mapPoint(x,z){return {x:24+(maxX-x)*sx,y:192-(z-startZ)*sz};}
export function mapHeading(vx,vz){return Math.atan2(-vx*sx,vz*sz)*180/Math.PI;}
export const mapRoutes=Object.fromEntries(Object.entries(lines).map(([id,points])=>[id,points.map((p,i)=>{const q=mapPoint(p.x,p.z);return `${i?'L':'M'}${q.x.toFixed(2)},${q.y.toFixed(2)}`;}).join(' ')]));
export const mapStart=mapPoint(ALPINE_REGION.start.x,startZ),mapFinish=mapPoint(ALPINE_REGION.finish.x,finishZ),mapChallenge=mapPoint(ALPINE_REGION.challenge.entrance.x,ALPINE_REGION.challenge.entrance.z);

// Camera framing only. Travel direction and steering never read this preview.
export function routePreview(state){
  const route=state.route==='pines'?'pines':'valley',p=routeCenter(route,state.z);
  const distance=Math.max(18,Math.min(28,state.speed*1.6));
  let z=state.z+distance*Math.cos(p.heading);
  const split=ALPINE_REGION.split;
  if(state.z<=split.startZ)z=Math.min(z,split.startZ);
  else if(state.z<split.endZ){
    const a=routeCenter('valley',state.z),b=routeCenter('pines',state.z);
    if(!state.routeChosen||Math.abs(a.x-b.x)<8||Math.abs(Math.abs(state.x-a.x)-Math.abs(state.x-b.x))<3)return null;
  }
  return routeCenter(route,z);
}

const cuePriority=cue=>['fork','challenge','finish'].includes(cue.kind)?2:cue.kind==='slow'?1:0;
const compareCues=(a,b)=>cuePriority(b)-cuePriority(a)||a.distance-b.distance;
const cueAllowed=(cue,state)=>!cue.challengeState||(Array.isArray(cue.challengeState)?cue.challengeState.includes(state.challenge):cue.challengeState===state.challenge);
const cueDistances=Object.fromEntries(Object.keys(lines).map(route=>[route,(ALPINE_REGION.cues||[]).filter(cue=>!cue.route||cue.route===route).map(cue=>{const p=routeCenter(route,cue.atZ);return {...cue,distance:routeProgress(route,p.x,p.z).distance};})]));
export function navigationState(state){
  const route=state.route==='pines'?'pines':'valley',progress=routeProgress(route,state.x,state.z);
  const section=ALPINE_REGION.sections.find(s=>state.z>=s.startZ&&state.z<s.endZ)||ALPINE_REGION.sections.at(-1);
  const cues=cueDistances[route].map(c=>({...c,remaining:c.distance-progress.distance})).filter(c=>cueAllowed(c,state)&&c.remaining>=-8&&c.remaining<=Math.max(c.leadMeters||90,state.speed*6.5)).sort(compareCues);
  // Choices and the finish take priority throughout the approach window. At
  // higher speed the distance grows with 6.5 seconds of actual travel speed.
  return {section:section.name,route,routeName:ALPINE_REGION.routes[route].name,travelled:progress.distance,point:mapPoint(state.x,state.z),heading:state.speed>.2?mapHeading(state.vx,state.vz):null,cues,cue:cues[0]||null};
}

// Reuse the caller's navigation sample; this state layer never projects the
// route again. dt is elapsed simulation time, not wall time while paused.
export function createRouteCueToast(){
  const seen=new Map();let active=null,age=0;
  function snapshot(){return {cue:active,phase:!active?'hidden':age<.18?'enter':age>=4.2?'exit':'hold',remainingSeconds:active?Math.max(0,4.5-age):0};}
  function reset(){seen.clear();active=null;age=0;return snapshot();}
  return {reset,update(state,nav,dt){
    if(state.status!=='playing'){active=null;age=0;return snapshot();}
    if(state.paused)return snapshot();
    const route=nav.route,travelled=nav.travelled,catalog=cueDistances[route]||[];
    for(const [id,record]of seen){
      if(record.route&&record.route!==route)continue;
      const marker=catalog.find(c=>c.id===id),remaining=(marker?.distance??record.distance)-travelled;
      if(remaining<=-15)record.passed=true;
      // Rearm only after passing, returning at least 25 m upstream, and then
      // advancing another 4 m. Waiting or dithering at a cue cannot replay it.
      if(record.passed&&remaining>=25&&record.returnDistance===null)record.returnDistance=travelled;
      if(record.returnDistance!==null){record.returnDistance=Math.min(record.returnDistance,travelled);if(travelled-record.returnDistance>=4)seen.delete(id);}
    }
    if(active){
      age+=Number.isFinite(dt)?Math.max(0,dt):0;
      const marker=catalog.find(c=>c.id===active.id)||active;
      active={...marker,remaining:marker.distance-travelled};
      if(active.route&&active.route!==route||!cueAllowed(active,state)||active.remaining< -8)age=Math.max(age,4.2);
      if(age>=4.5)active=null;
    }
    const next=nav.cues.filter(c=>c.remaining>=0&&!seen.has(c.id)).sort(compareCues)[0];
    if(next&&(!active||cuePriority(next)>cuePriority(active))){
      active={...next};age=0;seen.set(next.id,{distance:next.distance,route:next.route,passed:false,returnDistance:null});
    }
    return snapshot();
  }};
}
